/**
 * KML Loader Module
 * Handles loading and parsing KML files and converting to GeoJSON
 */

/**
 * Parse KML coordinates string to array
 * @param {string} coordsText - KML coordinates text
 * @returns {Array} Array of [lon, lat, alt?] coordinates
 */
function parseCoordinates(coordsText) {
    if (!coordsText) return [];

    return coordsText.trim().split(/\s+/).map(coord => {
        const parts = coord.split(',').map(parseFloat);
        // KML format is lon,lat,alt - return as [lon, lat] (ignore altitude for 2D)
        return parts.filter(n => !isNaN(n)).slice(0, 2);
    }).filter(coord => coord.length === 2);
}

/**
 * Extract text content from XML element
 * @param {Element} element - XML element
 * @param {string} tagName - Tag name to find
 * @returns {string} Text content or empty string
 */
function getElementText(element, tagName) {
    const el = element.getElementsByTagName(tagName)[0];
    return el ? el.textContent.trim() : '';
}

/**
 * Parse ExtendedData to properties object
 * @param {Element} placemark - Placemark element
 * @returns {Object} Properties object
 */
function parseExtendedData(placemark) {
    const properties = {};

    // Get basic properties
    const name = getElementText(placemark, 'name');
    const description = getElementText(placemark, 'description');

    if (name) properties.name = name;
    if (description) properties.description = description;

    // Parse ExtendedData elements
    const extendedData = placemark.getElementsByTagName('ExtendedData')[0];
    if (extendedData) {
        const dataElements = extendedData.getElementsByTagName('Data');
        for (let i = 0; i < dataElements.length; i++) {
            const data = dataElements[i];
            const name = data.getAttribute('name');
            const value = getElementText(data, 'value');
            if (name) {
                properties[name] = value;
            }
        }

        // Also check for SimpleData (used in some KML variants)
        const simpleData = extendedData.getElementsByTagName('SimpleData');
        for (let i = 0; i < simpleData.length; i++) {
            const data = simpleData[i];
            const name = data.getAttribute('name');
            const value = data.textContent.trim();
            if (name) {
                properties[name] = value;
            }
        }
    }

    return properties;
}

/**
 * Parse Point geometry
 * @param {Element} placemark - Placemark element
 * @returns {Object|null} GeoJSON Point geometry
 */
function parsePoint(placemark) {
    const point = placemark.getElementsByTagName('Point')[0];
    if (!point) return null;

    const coordsText = getElementText(point, 'coordinates');
    const coords = parseCoordinates(coordsText);

    if (coords.length === 0) return null;

    return {
        type: 'Point',
        coordinates: coords[0]
    };
}

/**
 * Parse LineString geometry
 * @param {Element} placemark - Placemark element
 * @returns {Object|null} GeoJSON LineString geometry
 */
function parseLineString(placemark) {
    const lineString = placemark.getElementsByTagName('LineString')[0];
    if (!lineString) return null;

    const coordsText = getElementText(lineString, 'coordinates');
    const coords = parseCoordinates(coordsText);

    if (coords.length < 2) return null;

    return {
        type: 'LineString',
        coordinates: coords
    };
}

/**
 * Parse Polygon geometry
 * @param {Element} placemark - Placemark element
 * @returns {Object|null} GeoJSON Polygon geometry
 */
function parsePolygon(placemark) {
    const polygon = placemark.getElementsByTagName('Polygon')[0];
    if (!polygon) return null;

    const rings = [];

    // Outer boundary
    const outerBoundary = polygon.getElementsByTagName('outerBoundaryIs')[0];
    if (outerBoundary) {
        const linearRing = outerBoundary.getElementsByTagName('LinearRing')[0];
        if (linearRing) {
            const coordsText = getElementText(linearRing, 'coordinates');
            const coords = parseCoordinates(coordsText);
            if (coords.length >= 3) {
                rings.push(coords);
            }
        }
    }

    // Inner boundaries (holes)
    const innerBoundaries = polygon.getElementsByTagName('innerBoundaryIs');
    for (let i = 0; i < innerBoundaries.length; i++) {
        const linearRing = innerBoundaries[i].getElementsByTagName('LinearRing')[0];
        if (linearRing) {
            const coordsText = getElementText(linearRing, 'coordinates');
            const coords = parseCoordinates(coordsText);
            if (coords.length >= 3) {
                rings.push(coords);
            }
        }
    }

    if (rings.length === 0) return null;

    return {
        type: 'Polygon',
        coordinates: rings
    };
}

/**
 * Parse MultiGeometry
 * @param {Element} placemark - Placemark element
 * @returns {Object|null} GeoJSON geometry
 */
function parseMultiGeometry(placemark) {
    const multiGeometry = placemark.getElementsByTagName('MultiGeometry')[0];
    if (!multiGeometry) return null;

    // For simplicity, extract the first geometry from MultiGeometry
    // You could extend this to handle true MultiPoint, MultiLineString, etc.
    const tempPlacemark = document.createElement('Placemark');

    // Try each geometry type
    if (multiGeometry.getElementsByTagName('Point')[0]) {
        tempPlacemark.appendChild(multiGeometry.getElementsByTagName('Point')[0].cloneNode(true));
        return parsePoint(tempPlacemark);
    }
    if (multiGeometry.getElementsByTagName('LineString')[0]) {
        tempPlacemark.appendChild(multiGeometry.getElementsByTagName('LineString')[0].cloneNode(true));
        return parseLineString(tempPlacemark);
    }
    if (multiGeometry.getElementsByTagName('Polygon')[0]) {
        tempPlacemark.appendChild(multiGeometry.getElementsByTagName('Polygon')[0].cloneNode(true));
        return parsePolygon(tempPlacemark);
    }

    return null;
}

/**
 * Convert KML to GeoJSON
 * @param {string} kmlText - KML text content
 * @returns {Object} GeoJSON FeatureCollection
 */
function kmlToGeoJSON(kmlText) {
    // Parse XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(kmlText, 'text/xml');

    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName('parsererror')[0];
    if (parseError) {
        throw new Error('Failed to parse KML: Invalid XML');
    }

    // Get all Placemarks
    const placemarks = xmlDoc.getElementsByTagName('Placemark');

    if (placemarks.length === 0) {
        throw new Error('No Placemarks found in KML file');
    }

    const features = [];

    // Parse each Placemark
    for (let i = 0; i < placemarks.length; i++) {
        const placemark = placemarks[i];

        // Try to parse different geometry types
        let geometry = parsePoint(placemark)
                    || parseLineString(placemark)
                    || parsePolygon(placemark)
                    || parseMultiGeometry(placemark);

        if (!geometry) {
            console.warn(`Placemark ${i} has no valid geometry, skipping`);
            continue;
        }

        // Parse properties
        const properties = parseExtendedData(placemark);
        properties.__featureIndex = features.length;

        // Create feature
        features.push({
            type: 'Feature',
            geometry,
            properties
        });
    }

    if (features.length === 0) {
        throw new Error('No valid features could be extracted from KML');
    }

    return {
        type: 'FeatureCollection',
        features
    };
}

/**
 * Load and parse KML file
 * @param {File} file - KML file
 * @param {Function} progressCallback - Progress callback (progress, message)
 * @returns {Object} Result with geojson, fileName, and featureCount
 */
export async function loadKML(file, progressCallback) {
    try {
        progressCallback(10, 'Reading KML file...');

        // Read file as text
        const text = await file.text();

        progressCallback(30, 'Parsing KML...');

        // Convert KML to GeoJSON
        const geojson = kmlToGeoJSON(text);

        progressCallback(70, 'Processing features...');

        // Get geometry types
        const geometryTypes = new Set();
        geojson.features.forEach(feature => {
            if (feature.geometry && feature.geometry.type) {
                geometryTypes.add(feature.geometry.type);
            }
        });

        progressCallback(90, 'Processing complete...');

        return {
            geojson,
            fileName: file.name.replace(/\.kml$/i, ''),
            featureCount: geojson.features.length,
            geometryTypes: Array.from(geometryTypes)
        };

    } catch (error) {
        console.error('Error loading KML:', error);
        throw error;
    }
}
